import React from 'react';
import { UserCircleIcon, ClipboardCheckIcon, PlaneIcon, HeartIcon, ArrowRightIcon } from 'lucide-react';
export function SimpleExperienceSection() {
  return <section className="py-20 bg-white" data-id="element-622">
      <div className="container mx-auto px-4" data-id="element-623">
        <div className="max-w-6xl mx-auto" data-id="element-624">
          <div className="text-center mb-16" data-id="element-625">
            <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-626">
              <span className="text-blue-700 font-semibold text-sm tracking-wide uppercase" data-id="element-627">
                Simple Experience
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-628">
              Feels Like Visiting{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600" data-id="element-629">
                Your Local Doctor
              </span>
            </h2>
            <p className="text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed mb-4" data-id="element-630">
              Nura coordinates all tests, medical records, and problem-solving
              so you mostly interact with one dedicated coordinator and your
              local doctor—until a short 3-6 day trip to the EU clinic for
              treatment.
            </p>
            <p className="text-slate-600 text-base max-w-2xl mx-auto" data-id="element-631">
              No complex international logistics. No language barriers. No
              stress. Just a guided path that feels familiar and simple.
            </p>
          </div>
          {/* What You Do vs What Nura Handles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12" data-id="element-632">
            {/* What You Do */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-3xl p-8" data-id="element-633">
              <div className="flex items-center gap-3 mb-6" data-id="element-634">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-3 rounded-2xl" data-id="element-635">
                  <UserCircleIcon className="text-white" size={28} data-id="element-636" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-637">
                  What You Do
                </h3>
              </div>
              <div className="space-y-6" data-id="element-638">
                <div className="flex gap-4" data-id="element-639">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1" data-id="element-640">
                    1
                  </div>
                  <div data-id="element-641">
                    <h4 className="font-bold text-slate-900 mb-1" data-id="element-642">
                      Initial Consultation
                    </h4>
                    <p className="text-slate-600 text-sm" data-id="element-643">
                      Talk to your coordinator and complete initial tests with
                      your local doctor at home
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-644">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1" data-id="element-645">
                    2
                  </div>
                  <div data-id="element-646">
                    <h4 className="font-bold text-slate-900 mb-1" data-id="element-647">
                      Prepare for Travel
                    </h4>
                    <p className="text-slate-600 text-sm" data-id="element-648">
                      Book your flights and accommodation based on the treatment
                      schedule we provide
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-649">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1" data-id="element-650">
                    3
                  </div>
                  <div data-id="element-651">
                    <h4 className="font-bold text-slate-900 mb-1" data-id="element-652">
                      3-6 Days in Europe
                    </h4>
                    <p className="text-slate-600 text-sm" data-id="element-653">
                      Travel for your treatment with our translator present for
                      all clinic visits
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-654">
                  <div className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 mt-1" data-id="element-655">
                    4
                  </div>
                  <div data-id="element-656">
                    <h4 className="font-bold text-slate-900 mb-1" data-id="element-657">
                      Return Home
                    </h4>
                    <p className="text-slate-600 text-sm" data-id="element-658">
                      Follow up with your local doctor for pregnancy test and
                      ongoing care
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* What Nura Handles */}
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-200 rounded-3xl p-8" data-id="element-659">
              <div className="flex items-center gap-3 mb-6" data-id="element-660">
                <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-3 rounded-2xl" data-id="element-661">
                  <ClipboardCheckIcon className="text-white" size={28} data-id="element-662" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-663">
                  What Nura Handles
                </h3>
              </div>
              <div className="space-y-4" data-id="element-664">
                <div className="flex items-start gap-3" data-id="element-665">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-666">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-667">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-668" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-669">
                    All legal paperwork and documentation
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-670">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-671">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-672">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-673" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-674">
                    Medical record transfers and translations
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-675">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-676">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-677">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-678" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-679">
                    Clinic appointment scheduling and coordination
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-680">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-681">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-682">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-683" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-684">
                    Treatment timeline optimization for minimal time away
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-685">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-686">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-687">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-688" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-689">
                    Professional translator for all clinic interactions
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-690">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-691">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-692">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-693" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-694">
                    Travel guidance and accommodation recommendations
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-695">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-696">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-697">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-698" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-699">
                    24/7 support during treatment and emergencies
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-700">
                  <div className="bg-emerald-600 p-1.5 rounded-full flex-shrink-0 mt-1" data-id="element-701">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-702">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-703" />
                    </svg>
                  </div>
                  <p className="text-slate-700 font-medium" data-id="element-704">
                    Post-treatment follow-up coordination
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* CTA */}
          <div className="text-center" data-id="element-705">
            <a href="/contact" className="inline-flex items-center gap-2 bg-blue-600 text-white px-10 py-5 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30 font-bold text-lg" data-id="element-706">
              Start Your Simple Journey
              <ArrowRightIcon size={20} data-id="element-707" />
            </a>
            <p className="text-slate-600 text-sm mt-4" data-id="element-708">
              Schedule a free consultation to learn how we make international
              IVF feel local
            </p>
          </div>
        </div>
      </div>
    </section>;
}